export const INVOICE_COMPARE_FIELDS = [
  { key: 'invoiceName', label: 'Invoice to' },
  { key: 'invoiceEmail', label: 'Invoice email' },
  { key: 'invoicePhone', label: 'Invoice phone' },
  { key: 'invoiceAddress', label: 'Invoice address' },
  { key: 'invoiceVat', label: 'VAT number' },
] as const

export type InvoiceCompareKey = (typeof INVOICE_COMPARE_FIELDS)[number]['key']

/** The customer-side invoice values a service is compared against. */
export type CustomerInvoiceValues = Partial<Record<InvoiceCompareKey, string>>

// Formatting is not a difference: case, runs of whitespace and (for phone
// numbers) spaces/dashes/brackets are ignored, so "072 628 4301" equals
// "0726284301" and "33 Monument St" equals "33 monument st".
const canon = (value: string | undefined, key?: InvoiceCompareKey) => {
  const text = (value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .trim()
    .toLowerCase()
  return key === 'invoicePhone' ? text.replace(/[^0-9+]/g, '') : text
}

export function invoiceFieldDiffers(
  serviceValue: string | undefined,
  customerValue: string | undefined,
  key?: InvoiceCompareKey,
): boolean {
  // A customer with nothing on file has nothing to offer: that is never a
  // difference worth flagging, and must never be used to blank a service
  // field.
  if (canon(customerValue, key) === '') return false
  return canon(serviceValue, key) !== canon(customerValue, key)
}

/** The compared fields whose customer value is present and different. */
export function differingInvoiceFields(
  service: CustomerInvoiceValues,
  customer: CustomerInvoiceValues | undefined,
) {
  if (!customer) return []
  return INVOICE_COMPARE_FIELDS.filter((field) =>
    invoiceFieldDiffers(service[field.key], customer[field.key], field.key),
  )
}

/**
 * Which customer to compare a service's invoice snapshot against: the one
 * picked in the form (even before saving, so a relink can be previewed) when
 * it carries invoice values, else the one the service is linked to.
 */
export function pickComparisonCustomer(args: {
  selected: ({ id: number | string | null } & Record<string, unknown>) | null
  currentCustomerId?: string | null
  linked?: CustomerInvoiceValues
}): CustomerInvoiceValues | undefined {
  const { selected, currentCustomerId, linked } = args
  if (
    selected &&
    selected.id != null &&
    String(selected.id) !== (currentCustomerId ?? '') &&
    INVOICE_COMPARE_FIELDS.some((field) => field.key in selected)
  ) {
    const out: CustomerInvoiceValues = {}
    for (const field of INVOICE_COMPARE_FIELDS) {
      out[field.key] = (selected[field.key] as string | undefined) ?? ''
    }
    return out
  }
  return linked
}
